import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { api, setToken } from '../api';
import { useAuth } from '../auth';
import { ErrorBox, Field, PageHead } from '../components/ui';
import { useI18n } from '../i18n';
import type { Lang } from '../strings';

export function SettingsPage() {
  const { t, lang, setLang } = useI18n();
  const { user, refresh } = useAuth();
  const [pw, setPw] = useState({ current: '', next: '', repeat: '' });
  const [done, setDone] = useState(false);
  const changePw = useMutation({
    mutationFn: () => api.post<{ token: string }>('/auth/change-password', { currentPassword: pw.current, newPassword: pw.next }),
    onSuccess: (res) => {
      setToken(res.token);
      setPw({ current: '', next: '', repeat: '' });
      setDone(true);
    },
  });
  const saveLang = useMutation({
    mutationFn: (l: Lang) => api.patch('/me', { language: l }),
    onSuccess: () => refresh(),
  });
  const mismatch = pw.repeat.length > 0 && pw.next !== pw.repeat;

  return (
    <>
      <PageHead title={t('settings.title')} />
      <div className="grid grid-2">
        <div className="card card-pad stack">
          <h2>{t('settings.profile')}</h2>
          <dl className="kv">
            <dt>{t('crew.name')}</dt><dd>{user?.name}</dd>
            <dt>{t('login.email')}</dt><dd>{user?.email}</dd>
            <dt>{t('crew.role')}</dt><dd>{t(`role.${user?.role}`)}</dd>
          </dl>
          <Field label={t('settings.language')} htmlFor="s-lang">
            <select
              id="s-lang"
              className="select"
              value={lang}
              onChange={(e) => {
                const l = e.target.value as Lang;
                setLang(l);
                saveLang.mutate(l);
              }}
            >
              <option value="sq">Shqip</option>
              <option value="en">English</option>
            </select>
          </Field>
        </div>
        <form
          className="card card-pad stack"
          onSubmit={(e) => {
            e.preventDefault();
            setDone(false);
            changePw.mutate();
          }}
        >
          <h2>{t('settings.changePassword')}</h2>
          {done && <div className="alert alert-ok">{t('settings.passwordChanged')}</div>}
          <ErrorBox error={changePw.error} />
          <Field label={t('settings.currentPassword')} htmlFor="s-cur">
            <input id="s-cur" className="input" type="password" autoComplete="current-password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} />
          </Field>
          <Field label={t('settings.newPassword')} hint={t('crew.passwordHint')} htmlFor="s-new">
            <input id="s-new" className="input" type="password" autoComplete="new-password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} />
          </Field>
          <Field label={t('settings.repeatPassword')} hint={mismatch ? t('settings.mismatch') : undefined} htmlFor="s-rep">
            <input id="s-rep" className="input" type="password" autoComplete="new-password" value={pw.repeat} onChange={(e) => setPw({ ...pw, repeat: e.target.value })} />
          </Field>
          <div>
            <button className="btn btn-primary" type="submit" disabled={!pw.current || pw.next.length < 8 || pw.next !== pw.repeat || changePw.isPending}>
              {t('settings.changePassword')}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
